import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Database,
  Users,
  Building2,
  AlertCircle,
  RefreshCw,
  ArrowRight,
  Check,
  Inbox,
  Activity,
} from "lucide-react";

import { useSeo } from "@/lib/useSeo";
import {
  maskName,
  maskEmail,
  maskPhone,
  formatRelativeTime,
  formatAbsoluteTime,
} from "@/lib/mask";
import DemoNotice from "@/components/site/DemoNotice";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const POLL_MS = 30000;
const GLOW_MS = 2200;

function useNoIndex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex,nofollow";
    document.head.appendChild(m);
    return () => {
      m.remove();
    };
  }, []);
}

/**
 * Generic polled data hook. Tracks the IDs of items "freshly arrived" in the
 * latest poll so the UI can briefly glow new rows.
 */
function useLiveData(endpoint) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [freshIds, setFreshIds] = useState(new Set());
  const [announce, setAnnounce] = useState("");
  const prevIdsRef = useRef(null);
  const intervalRef = useRef(null);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];

      const currentIds = new Set(list.map((d) => d.id).filter(Boolean));
      const firstLoad = prevIdsRef.current === null;
      let newIds = [];
      if (!firstLoad) {
        newIds = [...currentIds].filter((id) => !prevIdsRef.current.has(id));
      }
      prevIdsRef.current = currentIds;

      setItems(list);
      setStatus("success");
      setError(null);
      setLastUpdated(new Date());

      if (newIds.length > 0) {
        setFreshIds((prev) => {
          const next = new Set(prev);
          newIds.forEach((id) => next.add(id));
          return next;
        });
        setAnnounce(
          `${newIds.length} new ${newIds.length === 1 ? "lead" : "leads"} received`
        );
        setTimeout(() => {
          setFreshIds((prev) => {
            const next = new Set(prev);
            newIds.forEach((id) => next.delete(id));
            return next;
          });
        }, GLOW_MS);
        setTimeout(() => setAnnounce(""), 4000);
      }
    } catch (err) {
      setStatus("error");
      setError(err?.message || "Network error");
    }
  };

  useEffect(() => {
    let cancelled = false;

    const start = () => {
      if (intervalRef.current) return;
      fetchData();
      intervalRef.current = setInterval(() => {
        if (!cancelled) fetchData();
      }, POLL_MS);
    };
    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };

    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    items,
    status,
    error,
    lastUpdated,
    freshIds,
    announce,
    refetch: fetchData,
  };
}

// ============================================================
// UI primitives
// ============================================================

function SkeletonRow({ cells = 6 }) {
  return (
    <tr aria-hidden="true">
      {Array.from({ length: cells }).map((_, i) => (
        <td key={i} className="px-4 py-4">
          <div className="h-3 w-24 animate-pulse rounded-full bg-mist-line" />
        </td>
      ))}
    </tr>
  );
}

function SkeletonCard() {
  return (
    <li
      aria-hidden="true"
      className="rounded-2xl border border-mist-line bg-white p-5 shadow-soft"
    >
      <div className="h-3 w-32 animate-pulse rounded-full bg-mist-line" />
      <div className="mt-3 h-4 w-44 animate-pulse rounded-full bg-mist-line" />
      <div className="mt-3 h-3 w-28 animate-pulse rounded-full bg-mist-line" />
    </li>
  );
}

function EmptyCard({ message }) {
  return (
    <div
      data-testid="demo-leads-empty"
      className="flex flex-col items-center rounded-2xl border border-dashed border-mist-line bg-white px-6 py-10 text-center shadow-soft"
    >
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-mist text-navy/55">
        <Inbox className="h-5 w-5" />
      </span>
      <p className="mt-4 font-display text-lg font-bold tracking-tight text-navy">
        No demo leads yet.
      </p>
      <p className="mt-2 max-w-md text-sm text-navy/65">{message}</p>
    </div>
  );
}

function ErrorCard({ message, onRetry, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600">
          <AlertCircle className="h-4 w-4" />
        </span>
        <div>
          <p className="font-semibold">Couldn&rsquo;t load demo leads</p>
          <p className="text-red-700/85">{message}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white hover:bg-navy-soft"
      >
        <RefreshCw className="h-3 w-3" />
        Retry
      </button>
    </div>
  );
}

function NextStepPill({ value }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-sun/35 bg-sun/15 px-2 py-0.5 text-[11px] font-semibold text-sun">
      <span className="h-1 w-1 rounded-full bg-sun" />
      {value || "Callback to qualify"}
    </span>
  );
}

function PipelinePill({ value }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-aqua/12 px-2 py-0.5 text-[11px] font-semibold text-aqua-deep">
      {value || "New Lead \u2192 Qualifying"}
    </span>
  );
}

function FreshGlow({ children, fresh, className = "" }) {
  return (
    <motion.div
      animate={
        fresh
          ? {
              boxShadow: [
                "0 0 0 0 rgba(34,211,238,0.0)",
                "0 0 0 4px rgba(34,211,238,0.35)",
                "0 0 0 0 rgba(34,211,238,0.0)",
              ],
              backgroundColor: [
                "rgba(34,211,238,0.0)",
                "rgba(34,211,238,0.10)",
                "rgba(34,211,238,0.0)",
              ],
            }
          : {}
      }
      transition={{ duration: 2 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ============================================================
// Section A — Service Requests
// ============================================================

function ServiceRequestsSection() {
  const { items, status, error, lastUpdated, freshIds, announce, refetch } =
    useLiveData("/api/service-requests?limit=50");

  return (
    <section
      data-testid="demo-leads-service-section"
      aria-labelledby="leads-service-heading"
      className="relative bg-white py-16 sm:py-20"
    >
      <div className="container-page">
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <span className="pill">
              <Users className="h-3 w-3 text-aqua-deep" />
              Section A
            </span>
            <h2
              id="leads-service-heading"
              className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Service requests
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-navy/65 sm:text-base">
              Last 50 from the homeowner intake form. Masked for privacy.
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs text-navy/55">
            <span
              data-testid="demo-leads-service-updated"
              className="inline-flex items-center gap-1.5"
            >
              <Activity className="h-3 w-3 text-aqua-deep" />
              {lastUpdated
                ? `Updated ${formatRelativeTime(lastUpdated)}`
                : "Loading…"}
            </span>
            <button
              type="button"
              onClick={refetch}
              data-testid="demo-leads-service-refresh"
              className="inline-flex items-center gap-1 rounded-full border border-mist-line bg-white px-2.5 py-1 text-xs font-semibold text-navy/75 hover:border-aqua/45 hover:text-navy"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh
            </button>
          </div>
        </div>

        <div
          aria-live="polite"
          aria-atomic="true"
          data-testid="demo-leads-service-announcer"
          className="sr-only"
        >
          {announce}
        </div>

        <div className="mt-8">
          {status === "error" && (
            <ErrorCard
              message={error}
              onRetry={refetch}
              testId="demo-leads-service-error"
            />
          )}

          {status !== "error" && status !== "loading" && items.length === 0 && (
            <EmptyCard message="Try submitting the form at /request-service to see how new leads appear here." />
          )}

          {(status === "loading" || items.length > 0) && status !== "error" && (
            <>
              {/* DESKTOP TABLE */}
              <div
                className="hidden overflow-hidden rounded-2xl border border-mist-line bg-white shadow-soft md:block"
                data-testid="demo-leads-service-table-wrap"
              >
                <table
                  className="w-full text-left text-sm"
                  data-testid="demo-leads-service-table"
                  aria-busy={status === "loading"}
                >
                  <thead className="bg-mist-soft text-[11px] uppercase tracking-[0.12em] text-navy/55">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-semibold">Created</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Customer</th>
                      <th scope="col" className="px-4 py-3 font-semibold">City</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Issue</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Urgency</th>
                      <th scope="col" className="px-4 py-3 font-semibold">System age</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Cooling</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Photo</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Next step</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Pipeline</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-mist-line">
                    {status === "loading" &&
                      Array.from({ length: 4 }).map((_, i) => (
                        <SkeletonRow key={i} cells={10} />
                      ))}
                    {items.map((it) => {
                      const fresh = freshIds.has(it.id);
                      return (
                        <motion.tr
                          key={it.id}
                          data-testid={`demo-leads-service-row-${it.id}`}
                          data-fresh={fresh ? "true" : undefined}
                          animate={
                            fresh
                              ? {
                                  backgroundColor: [
                                    "rgba(34,211,238,0.18)",
                                    "rgba(34,211,238,0.0)",
                                  ],
                                }
                              : {}
                          }
                          transition={{ duration: 2 }}
                          className="text-[13px] text-navy/90"
                        >
                          <td
                            className="whitespace-nowrap px-4 py-3 text-navy/65"
                            title={formatAbsoluteTime(it.created_at)}
                          >
                            {formatRelativeTime(it.created_at)}
                          </td>
                          <td className="px-4 py-3 font-semibold text-navy">
                            {maskName(it.full_name)}
                          </td>
                          <td className="px-4 py-3">{it.city || "\u2014"}</td>
                          <td className="px-4 py-3">{it.issue_type || "\u2014"}</td>
                          <td className="px-4 py-3">{it.urgency || "\u2014"}</td>
                          <td className="px-4 py-3">{it.system_age || "\u2014"}</td>
                          <td className="px-4 py-3">{it.is_cooling_now || "\u2014"}</td>
                          <td className="px-4 py-3 text-center">
                            {it.photo_filename ? (
                              <Check className="inline h-4 w-4 text-aqua-deep" />
                            ) : (
                              <span className="text-navy/40">{"\u2014"}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <NextStepPill value={it.recommended_next_step} />
                          </td>
                          <td className="px-4 py-3">
                            <PipelinePill value={it.pipeline_status} />
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARD LIST */}
              <ul
                className="space-y-3 md:hidden"
                aria-busy={status === "loading"}
                data-testid="demo-leads-service-cards"
              >
                {status === "loading" &&
                  Array.from({ length: 3 }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                {items.map((it) => {
                  const fresh = freshIds.has(it.id);
                  return (
                    <li key={it.id}>
                      <FreshGlow
                        fresh={fresh}
                        className="rounded-2xl border border-mist-line bg-white p-4 shadow-soft"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <p
                            className="text-[11px] font-medium uppercase tracking-[0.14em] text-navy/55"
                            title={formatAbsoluteTime(it.created_at)}
                          >
                            {formatRelativeTime(it.created_at)}
                          </p>
                          {it.photo_filename && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-aqua-deep">
                              <Check className="h-3 w-3" />
                              Photo
                            </span>
                          )}
                        </div>
                        <p className="mt-1 font-display text-base font-bold tracking-tight text-navy">
                          {maskName(it.full_name)}
                          <span className="ml-2 text-xs font-medium text-navy/55">
                            {it.city}
                          </span>
                        </p>
                        <p className="mt-1 text-[13px] text-navy/80">
                          {it.issue_type}{" "}
                          <span className="text-navy/55">·</span>{" "}
                          {it.urgency}
                        </p>
                        <p className="mt-1 text-[12px] text-navy/55">
                          {it.system_age} · cooling: {it.is_cooling_now}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <NextStepPill value={it.recommended_next_step} />
                          <PipelinePill value={it.pipeline_status} />
                        </div>
                      </FreshGlow>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Section B — Contractor Inquiries
// ============================================================

function ContractorInquiriesSection() {
  const { items, status, error, lastUpdated, freshIds, announce, refetch } =
    useLiveData("/api/contractor-inquiries?limit=50");

  return (
    <section
      data-testid="demo-leads-contractor-section"
      aria-labelledby="leads-contractor-heading"
      className="relative bg-mist py-16 sm:py-20"
    >
      <div className="container-page">
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <span className="pill">
              <Building2 className="h-3 w-3 text-aqua-deep" />
              Section B
            </span>
            <h2
              id="leads-contractor-heading"
              className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Contractor inquiries
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-navy/65 sm:text-base">
              HVAC business owners interested in PrismForge Digital. Masked for
              privacy.
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs text-navy/55">
            <span
              data-testid="demo-leads-contractor-updated"
              className="inline-flex items-center gap-1.5"
            >
              <Activity className="h-3 w-3 text-aqua-deep" />
              {lastUpdated
                ? `Updated ${formatRelativeTime(lastUpdated)}`
                : "Loading…"}
            </span>
            <button
              type="button"
              onClick={refetch}
              data-testid="demo-leads-contractor-refresh"
              className="inline-flex items-center gap-1 rounded-full border border-mist-line bg-white px-2.5 py-1 text-xs font-semibold text-navy/75 hover:border-aqua/45 hover:text-navy"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh
            </button>
          </div>
        </div>

        <div
          aria-live="polite"
          aria-atomic="true"
          data-testid="demo-leads-contractor-announcer"
          className="sr-only"
        >
          {announce}
        </div>

        <div className="mt-8">
          {status === "error" && (
            <ErrorCard
              message={error}
              onRetry={refetch}
              testId="demo-leads-contractor-error"
            />
          )}

          {status !== "error" && status !== "loading" && items.length === 0 && (
            <EmptyCard message="Try submitting the form at /demo to see how new contractor inquiries appear here." />
          )}

          {(status === "loading" || items.length > 0) && status !== "error" && (
            <>
              {/* DESKTOP TABLE */}
              <div
                className="hidden overflow-hidden rounded-2xl border border-mist-line bg-white shadow-soft md:block"
                data-testid="demo-leads-contractor-table-wrap"
              >
                <table
                  className="w-full text-left text-sm"
                  data-testid="demo-leads-contractor-table"
                  aria-busy={status === "loading"}
                >
                  <thead className="bg-mist-soft text-[11px] uppercase tracking-[0.12em] text-navy/55">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-semibold">Created</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Name</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Email</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Phone</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Company</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Business</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-mist-line">
                    {status === "loading" &&
                      Array.from({ length: 4 }).map((_, i) => (
                        <SkeletonRow key={i} cells={7} />
                      ))}
                    {items.map((it) => {
                      const fresh = freshIds.has(it.id);
                      return (
                        <motion.tr
                          key={it.id}
                          data-testid={`demo-leads-contractor-row-${it.id}`}
                          data-fresh={fresh ? "true" : undefined}
                          animate={
                            fresh
                              ? {
                                  backgroundColor: [
                                    "rgba(34,211,238,0.18)",
                                    "rgba(34,211,238,0.0)",
                                  ],
                                }
                              : {}
                          }
                          transition={{ duration: 2 }}
                          className="text-[13px] text-navy/90"
                        >
                          <td
                            className="whitespace-nowrap px-4 py-3 text-navy/65"
                            title={formatAbsoluteTime(it.created_at)}
                          >
                            {formatRelativeTime(it.created_at)}
                          </td>
                          <td className="px-4 py-3 font-semibold text-navy">
                            {maskName(it.full_name)}
                          </td>
                          <td className="px-4 py-3 font-mono text-[12px] text-navy/80">
                            {maskEmail(it.email)}
                          </td>
                          <td className="px-4 py-3 font-mono text-[12px] text-navy/80">
                            {maskPhone(it.phone)}
                          </td>
                          <td className="px-4 py-3">{it.company_name || "\u2014"}</td>
                          <td className="px-4 py-3">{it.business_type || "\u2014"}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 rounded-full border border-aqua/30 bg-aqua/12 px-2 py-0.5 text-[11px] font-semibold text-aqua-deep">
                              {it.source || "sun-chaser-demo"}
                            </span>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARDS */}
              <ul
                className="space-y-3 md:hidden"
                aria-busy={status === "loading"}
                data-testid="demo-leads-contractor-cards"
              >
                {status === "loading" &&
                  Array.from({ length: 3 }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                {items.map((it) => {
                  const fresh = freshIds.has(it.id);
                  return (
                    <li key={it.id}>
                      <FreshGlow
                        fresh={fresh}
                        className="rounded-2xl border border-mist-line bg-white p-4 shadow-soft"
                      >
                        <p
                          className="text-[11px] font-medium uppercase tracking-[0.14em] text-navy/55"
                          title={formatAbsoluteTime(it.created_at)}
                        >
                          {formatRelativeTime(it.created_at)}
                        </p>
                        <p className="mt-1 font-display text-base font-bold tracking-tight text-navy">
                          {maskName(it.full_name)}
                          <span className="ml-2 text-xs font-medium text-navy/55">
                            {it.business_type}
                          </span>
                        </p>
                        <p className="mt-1 text-[13px] text-navy/80">
                          {it.company_name}
                        </p>
                        <p className="mt-1 font-mono text-[12px] text-navy/65">
                          {maskEmail(it.email)} · {maskPhone(it.phone)}
                        </p>
                        <p className="mt-2 inline-flex items-center gap-1 rounded-full border border-aqua/30 bg-aqua/12 px-2 py-0.5 text-[11px] font-semibold text-aqua-deep">
                          {it.source || "sun-chaser-demo"}
                        </p>
                      </FreshGlow>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Page hero + closing disclaimer
// ============================================================

function LeadsHero() {
  return (
    <section
      data-testid="demo-leads-hero"
      aria-labelledby="demo-leads-h1"
      className="relative overflow-hidden border-b border-mist-line bg-white"
    >
      <div className="absolute inset-0 bg-brand-gradient-soft opacity-90" aria-hidden="true" />
      <div
        className="absolute inset-0 opacity-80"
        style={{
          backgroundImage:
            "radial-gradient(circle at 12% 18%, rgba(34,211,238,0.16) 0%, transparent 48%), radial-gradient(circle at 88% 86%, rgba(245,158,11,0.10) 0%, transparent 55%)",
        }}
        aria-hidden="true"
      />
      <div className="container-page relative py-10 sm:py-14">
        <span className="pill">
          <Database className="h-3 w-3 text-aqua-deep" />
          Demo data preview
        </span>
        <h1
          id="demo-leads-h1"
          className="mt-4 max-w-3xl font-display text-3xl font-bold leading-[1.06] tracking-tight text-navy sm:text-4xl lg:text-5xl"
        >
          Live demo leads
        </h1>
        <p className="mt-4 max-w-2xl text-base text-navy/70 sm:text-lg">
          This view shows how an HVAC company could see organized service and
          contractor inquiries coming in. Personal details are masked for demo
          privacy.
        </p>
        <div className="mt-5">
          <DemoNotice
            variant="dark"
            label="Demo data only — masked for privacy. Built by PrismForge Digital."
          />
        </div>
      </div>
    </section>
  );
}

function ClosingDisclaimer() {
  return (
    <section
      data-testid="demo-leads-closing"
      className="relative bg-white py-16 sm:py-20"
    >
      <div className="container-page">
        <div className="relative overflow-hidden rounded-3xl border border-aqua/20 bg-brand-gradient p-8 text-white shadow-lift sm:p-10">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-aqua/30 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-14 -left-10 h-56 w-56 rounded-full bg-sun/15 blur-3xl" aria-hidden="true" />
          <div className="relative grid grid-cols-1 items-center gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <span className="pill-dark">
                <Database className="h-3 w-3 text-sun" />
                Demo privacy
              </span>
              <h2 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
                These are sample leads &mdash; personal data is masked.
              </h2>
              <p className="mt-3 max-w-xl text-sm text-white/80 sm:text-base">
                Every record above is flagged <code className="rounded bg-white/10 px-1.5 py-0.5 text-aqua/90">is_demo: true</code>{" "}
                on the backend. Names, emails, and phones are masked client-side
                before they ever reach this view. Notes are never rendered
                publicly.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:col-span-4 lg:justify-end">
              <Link
                to="/demo"
                data-testid="demo-leads-cta-back"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-sun px-5 py-3 text-sm font-semibold text-navy-deep shadow-sunGlow transition-transform hover:scale-[1.02]"
              >
                See how this demo works
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Page
// ============================================================

export default function DemoLeads() {
  useSeo({
    title:
      "Live demo leads | Sun Chaser Cooling \u2014 PrismForge Digital Demo",
    description:
      "Preview of how an HVAC company could see organized service requests and contractor inquiries. Demo data with personal details masked for privacy.",
  });
  useNoIndex();

  return (
    <div data-testid="demo-leads-page">
      <LeadsHero />
      <ServiceRequestsSection />
      <ContractorInquiriesSection />
      <ClosingDisclaimer />
    </div>
  );
}
